import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// ─── Constantes ──────────────────────────────────────────────────────────────

/** Zone (px) depuis le bord gauche qui déclenche la détection */
const EDGE_ZONE = 25
/** Distance (px) à parcourir pour valider le swipe */
const THRESHOLD = 80

/** Pages des 4 tabs principaux — PAS de swipe back */
const TAB_PATHS = new Set([
  '/app',
  '/app/prayer',
  '/app/journal',
  '/app/profile',
])

// ─── Types ───────────────────────────────────────────────────────────────────

interface Options {
  /** Permet de désactiver le swipe de force depuis l'extérieur (optionnel) */
  enabled?: boolean
  /** Fallback si l'historique est vide (deep link direct). Défaut : navigate(-1) */
  targetPath?: string
}

/**
 * Swipe back natif iOS simplifié : détecte UNIQUEMENT le geste et appelle
 * navigate(-1). Aucune animation de page — pas de conflit avec React Router.
 *
 * - Détection depuis le bord gauche (clientX < 25px)
 * - Validation : deltaX > 80 ET horizontal (|deltaX| > |deltaY|)
 * - Désactivé sur les 4 tabs principaux (TAB_PATHS)
 * - Désactivé si une modale est ouverte ([data-modal="true"] dans le DOM)
 * - Navigation instantanée = aucun flash, aucun écran noir
 */
export function useSwipeBack(options: Options = {}) {
  const { enabled = true, targetPath = '/app' } = options
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Pas de swipe sur les tabs principaux ni si explicitement désactivé
    if (!enabled) return
    if (TAB_PATHS.has(location.pathname)) return

    let startX = 0
    let startY = 0
    let tracking = false

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      // Zone bord gauche uniquement
      if (t.clientX > EDGE_ZONE) return
      // Désactivé si une modale est ouverte
      if (document.querySelector('[data-modal="true"]')) return
      startX = t.clientX
      startY = t.clientY
      tracking = true
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return
      tracking = false
      const t = e.changedTouches[0]
      if (!t) return
      const deltaX = t.clientX - startX
      const deltaY = Math.abs(t.clientY - startY)
      // Swipe horizontal suffisant vers la droite + pas un scroll vertical
      if (deltaX > THRESHOLD && deltaY < deltaX) {
        // navigate(-1) = retour à la page précédente (comportement iOS natif)
        if (window.history.length > 1) {
          navigate(-1)
        } else {
          navigate(targetPath)
        }
      }
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchend',   onTouchEnd,   { passive: true })
    document.addEventListener('touchcancel', () => { tracking = false }, { passive: true })

    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchend',   onTouchEnd)
    }
  }, [enabled, navigate, location.pathname, targetPath])
}
